import {
  AfterViewInit, Component, ElementRef, HostBinding, OnInit,
} from '@angular/core';
import { DomSanitizer, Meta } from '@angular/platform-browser';
import { filter } from 'rxjs/operators';
import { enableAkitaProdMode } from '@datorama/akita';
import { MatIconRegistry } from '@angular/material/icon';
import { NavigationEnd, Router } from '@angular/router';
import { UnloadProvider } from 'src/providers/unloadProvider';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import '@fullcalendar/web-component/global';
import { registerSmartBreak } from '../assets/quill/shift-enter-binding';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslateService } from './utils/translate.service';
import { NavigationEventsProvider } from 'src/providers/navigation-events.provider';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { version } from 'packageJson';

const QUILL_EDITOR_CSS_TRANSLATIONS = {
  '--i18n-change': _('generic.change'),
  '--i18n-save': _('generic.save'),
  '--i18n-remove': _('generic.remove'),
  '--i18n-quill-link-label': _('quill.link.label'),
};

export const redirectMappings: { [key: string]: string } = {
  'blossombeinspired-admin-test.firebaseapp.com': 'https://ovivio-activities-admin-test.web.app',
  'online.blossombeinspired.co.uk': 'https://activities.uk.ovivio.com',
};

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, AfterViewInit {
  title = 'doenkids-admin-portal';

  @HostBinding('attr.app-version') appVersion = version;

  initialized$ = new BehaviorSubject<boolean>(false);

  constructor(
    private elementRef: ElementRef,
    private $translate: TranslateService,
    private $unload: UnloadProvider,
    private gtmService: GoogleTagManagerService,
    private iconRegistry: MatIconRegistry,
    private router: Router,
    private sanitizer: DomSanitizer,
    private navigationEventsProvider: NavigationEventsProvider,
    private metaService: Meta,
  ) {
    this.setMetaTags();
  }

  async ngOnInit() {
    enableAkitaProdMode();

    // Check for redirect conditions
    this.checkRedirect();

    // Track to initialization event
    //
    try {
      this.gtmService.pushTag({ event: 'init' });
    } catch (e) {
      // if something goes wrong here though luck
      console.log('google tag manager failed', e);
    }


    // Send page events to Google Tag Manager
    //
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
    ).subscribe((event) => {
      try {
        this.gtmService.pushTag({
          event: 'pageview',
          pageName: (event as NavigationEnd).url,
        });
      } catch (e) {
        // if something goes wrong here though luck
        console.log('google tag manager failed', e);
      }
    });


    if (window.location.hostname !== 'localhost') {
      this.navigationEventsProvider.trackNavigationEvents();
    }

    this.iconRegistry.addSvgIcon('doenkids-customer', this.sanitizer.bypassSecurityTrustResourceUrl('./assets/icons/ic_customer.svg'));
    this.iconRegistry.addSvgIcon('doenkids-location', this.sanitizer.bypassSecurityTrustResourceUrl('./assets/icons/ic_location.svg'));
    this.iconRegistry.addSvgIcon('doenkids-group', this.sanitizer.bypassSecurityTrustResourceUrl('./assets/icons/ic_group.svg'));
    this.iconRegistry.addSvgIcon('ou-type-1', this.sanitizer.bypassSecurityTrustResourceUrl('./assets/icons/ic_node.svg'));
    this.iconRegistry.addSvgIcon('ou-type-2', this.sanitizer.bypassSecurityTrustResourceUrl('./assets/icons/ic_customer.svg'));
    this.iconRegistry.addSvgIcon('ou-type-3', this.sanitizer.bypassSecurityTrustResourceUrl('./assets/icons/ic_location.svg'));
    this.iconRegistry.addSvgIcon('ou-type-4', this.sanitizer.bypassSecurityTrustResourceUrl('./assets/icons/ic_group.svg'));
    this.iconRegistry.addSvgIcon('jibbie-linked', this.sanitizer.bypassSecurityTrustResourceUrl('./assets/icons/ic_jibbie_linked_24px.svg'));
    this.iconRegistry.addSvgIcon('jibbie-unlinked', this.sanitizer.bypassSecurityTrustResourceUrl('./assets/icons/ic_jibbie_unlinked_24px.svg'));
    this.iconRegistry.addSvgIcon('konnect-linked', this.sanitizer.bypassSecurityTrustResourceUrl('./assets/icons/ic_konnect_linked_24px.svg'));
    this.iconRegistry.addSvgIcon('konnect-unlinked', this.sanitizer.bypassSecurityTrustResourceUrl('./assets/icons/ic_konnect_unlinked_24px.svg'));
    this.iconRegistry.addSvgIcon('excel', this.sanitizer.bypassSecurityTrustResourceUrl('./assets/icons/excel.svg'));
    this.iconRegistry.addSvgIcon('nl', this.sanitizer.bypassSecurityTrustResourceUrl('./assets/icons/languages/nl.svg'));
    this.iconRegistry.addSvgIcon('fr', this.sanitizer.bypassSecurityTrustResourceUrl('./assets/icons/languages/fr.svg'));
    this.iconRegistry.addSvgIcon('en', this.sanitizer.bypassSecurityTrustResourceUrl('./assets/icons/languages/en.svg'));

    // Ovivio custom icons
    this.iconRegistry.addSvgIcon('ovivio-notebook', this.sanitizer.bypassSecurityTrustResourceUrl('./assets/icons/ovivio/notebook-bold.svg'));
    this.iconRegistry.addSvgIcon('ovivio-book-open', this.sanitizer.bypassSecurityTrustResourceUrl('./assets/icons/ovivio/book-open-text-bold.svg'));
    this.iconRegistry.addSvgIcon('ovivio-calendar', this.sanitizer.bypassSecurityTrustResourceUrl('./assets/icons/ovivio/calendar-blank-bold.svg'));
    this.iconRegistry.addSvgIcon('ovivio-push-pin', this.sanitizer.bypassSecurityTrustResourceUrl('./assets/icons/ovivio/push-pin-simple-bold.svg'));
    this.iconRegistry.addSvgIcon('ovivio-user-square', this.sanitizer.bypassSecurityTrustResourceUrl('./assets/icons/ovivio/user-square-bold.svg'));
    this.iconRegistry.addSvgIcon('ovivio-building', this.sanitizer.bypassSecurityTrustResourceUrl('./assets/icons/ovivio/building-office-bold.svg'));
    this.iconRegistry.addSvgIcon('ovivio-gear', this.sanitizer.bypassSecurityTrustResourceUrl('./assets/icons/ovivio/gear-bold.svg'));
    this.iconRegistry.addSvgIcon('ovivio-user-check', this.sanitizer.bypassSecurityTrustResourceUrl('./assets/icons/ovivio/user-circle-check-bold.svg'));
    this.iconRegistry.addSvgIcon('ovivio-footprints', this.sanitizer.bypassSecurityTrustResourceUrl('./assets/icons/ovivio/footprints-bold.svg'));
    this.iconRegistry.addSvgIcon('ovivio-newspaper', this.sanitizer.bypassSecurityTrustResourceUrl('./assets/icons/ovivio/newspaper-bold.svg'));
    this.iconRegistry.addSvgIcon('ovivio-sidebar', this.sanitizer.bypassSecurityTrustResourceUrl('./assets/icons/ovivio/sidebar-simple-bold.svg'));

    registerSmartBreak();
  }

  ngAfterViewInit(): void {
    this.$translate
      .onInitialTranslationAndLangOrTranslationChange$
      .subscribe((event) => {
        if (!this.initialized$.value) {
          this.initialized$.next(true);
        }
        this.updateQuillEditorCssTranslations(event.translations);
      });
  }

  doBeforeUnload() {
    this.$unload.unload();
    return this.$unload.canUnload;
  }

  private updateQuillEditorCssTranslations(translations: any): void {
    Object.keys(QUILL_EDITOR_CSS_TRANSLATIONS).forEach((cssVariableName) => {
      const translationKey = QUILL_EDITOR_CSS_TRANSLATIONS[cssVariableName];
      const translation = translations[translationKey];
      if (translation && translation !== translationKey) {
        this.elementRef.nativeElement.style.setProperty(cssVariableName, `'${translation}'`);
      } else {
        this.elementRef.nativeElement.style.removeProperty(cssVariableName);
      }
    });
  }

  private setMetaTags() {
    this.metaService.updateTag({ property: 'og:locale', content: environment.defaultLanguage });
    this.metaService.updateTag({ property: 'og:url', content: [document.location.protocol, '//', document.location.host, '/'].join('') });
  }

  private checkRedirect() {
    const hostname = window.location.hostname;
    const port = window.location.port;

    if (port) {
      const hostWithPort = `${hostname}:${port}`;
      if (redirectMappings[hostWithPort]) {
        this.router.navigate(['/redirect'], {
          queryParams: { url: redirectMappings[hostWithPort] },
        });
        return;
      }
    } else {
      if (redirectMappings[hostname]) {
        this.router.navigate(['/redirect'], {
          queryParams: { url: redirectMappings[hostname] },
        });
        return;
      }
    }
  }
}
